import { useRef, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Loader2, X, Check, FileText } from "lucide-react";
import { toast } from "sonner";

const DOC_LABELS = {
  constancia_fiscal: "Constancia de Situación Fiscal (SAT)",
  acta_constitutiva: "Acta Constitutiva",
  poder_notarial: "Poder Notarial",
  otro: "Documento",
};

const FIELD_LABELS = {
  razon_social: "Razón social", rfc: "RFC", regimen_fiscal: "Régimen fiscal",
  regimen_capital: "Régimen de capital", nombre_comercial: "Nombre comercial", id_cif: "idCIF",
  estatus_padron: "Estatus en el padrón", fecha_inicio_operaciones: "Inicio de operaciones",
  domicilio_fiscal: "Domicilio fiscal", codigo_postal: "C.P.", colonia: "Colonia",
  municipio: "Municipio", entidad_federativa: "Entidad federativa",
  notario: "Notario", notaria_numero: "Notaría No.", notaria_lugar: "Lugar de la notaría",
  instrumento_numero: "Instrumento No.", fecha_constitucion: "Fecha de constitución",
  folio_mercantil: "Folio mercantil", registro_publico: "Registro Público",
  objeto_social: "Objeto social", capital_social: "Capital social",
  duracion_sociedad: "Duración", tipo_administracion: "Administración",
};

export const AIProfileReader = ({ onApply }) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/legal/profile/ai-extract", fd, { timeout: 120000 });
      if (!Object.keys(r.data.fields || {}).length && !(r.data.socios || []).length) {
        toast.warning("La IA no encontró datos reconocibles en el documento");
      }
      setResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo leer el documento con IA");
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    onApply(result);
    setResult(null);
    toast.success("Datos aplicados al formulario. Revisa y guarda el expediente.");
  };

  return (
    <>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
        data-testid="legal-ai-file-input" onChange={handleFile} />
      <button data-testid="legal-ai-read-btn" onClick={() => inputRef.current?.click()} disabled={loading}
        className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-2">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {loading ? "Leyendo documento…" : "Leer con IA"}
      </button>
      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="legal-ai-preview-modal">
          <div className="panel w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-300" />
                <div>
                  <div className="text-sm font-semibold text-cyan-300">{DOC_LABELS[result.doc_type] || "Documento"}</div>
                  <div className="text-[11px] text-slate-400">{result.filename} — {result.resumen}</div>
                </div>
              </div>
              <button onClick={() => setResult(null)} className="text-slate-400 hover:text-white" data-testid="legal-ai-close-btn"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 overflow-y-auto space-y-3 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5">
                {Object.entries(result.fields || {}).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="label-mono shrink-0">{FIELD_LABELS[k] || k}:</span>
                    <span className="text-slate-200 break-words">{v}</span>
                  </div>
                ))}
              </div>
              {(result.actividades_economicas || []).length > 0 && (
                <div>
                  <div className="label-mono mb-1">// ACTIVIDADES ECONÓMICAS</div>
                  {result.actividades_economicas.map((a, i) => (
                    <div key={i} className="text-slate-300">• {a.actividad} {a.porcentaje && `(${a.porcentaje}%)`}</div>
                  ))}
                </div>
              )}
              {(result.socios || []).length > 0 && (
                <div>
                  <div className="label-mono mb-1">// SOCIOS / ACCIONISTAS</div>
                  {result.socios.map((s, i) => (
                    <div key={i} className="text-slate-300">• {s.nombre} — {s.acciones && `${s.acciones} acciones`} {s.monto && `(${s.monto})`}</div>
                  ))}
                </div>
              )}
              {(result.representantes || []).length > 0 && (
                <div>
                  <div className="label-mono mb-1">// REPRESENTANTES DETECTADOS</div>
                  {result.representantes.map((r, i) => (
                    <div key={i} className="text-slate-300">• {r.name} — {r.position}</div>
                  ))}
                  <div className="text-[10px] text-slate-500 mt-1 italic">Los representantes se registran en la pestaña "Representantes Legales"; aquí solo se muestran como referencia.</div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t" style={{ borderColor: "var(--border)" }}>
              <button onClick={() => setResult(null)} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest">Descartar</button>
              <button data-testid="legal-ai-apply-btn" onClick={apply}
                className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1.5">
                <Check className="w-4 h-4" /> Aplicar al formulario
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
